import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const GST = 0.15;
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const inclOf = (item, rate) => {
  if (item.incl != null && item.incl !== "") return Number(item.incl);
  if (item.hours != null && item.hours !== "") return round2(Number(item.hours) * rate * (1 + GST));
  if (item.amount != null && item.amount !== "") return round2(Number(item.amount) * (1 + GST));
  return 0;
};

export default function ChargesPanel() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/charges").then((r) => setC(r.data.charges));
  useEffect(() => { load(); }, []);
  if (!c) return <Loader2 className="animate-spin text-[#B89874] mt-8" />;

  const rate = Number(c.hourly_rate) || 0;
  const setSec = (si, next) => setC((s) => ({ ...s, sections: s.sections.map((x, i) => (i === si ? next : x)) }));
  const setItem = (si, ii, next) => setSec(si, { ...c.sections[si], items: c.sections[si].items.map((x, j) => (j === ii ? next : x)) });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...c, hourly_rate: Number(c.hourly_rate) };
      await api.put("/charges", { charges: payload });
      toast.success("Charges saved. The Schedule of Charges document is updated.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset all charges to the standard defaults?")) return;
    setBusy(true);
    try { const { data } = await api.post("/charges/reset"); setC(data.charges); toast.success("Charges reset to defaults."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Schedule of Charges</h1>
          <p className="mt-2 text-[#58635C]">Set the default fees. GST-inclusive figures are calculated for you and the Schedule document updates automatically.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={busy} data-testid="charges-reset" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors disabled:opacity-60"><RotateCcw size={15} /> Reset</button>
          <button onClick={save} disabled={busy} data-testid="charges-save" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[#E5E1D8] p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Standard hourly rate ($)</label>
            <input data-testid="charges-rate" type="number" step="0.01" value={c.hourly_rate}
              onChange={(e) => setC({ ...c, hourly_rate: e.target.value })}
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Intro paragraph</label>
          <textarea data-testid="charges-intro" rows={3} value={c.intro} onChange={(e) => setC({ ...c, intro: e.target.value })}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y text-sm" />
        </div>
      </div>

      {c.sections.map((sec, si) => (
        <div key={si} data-testid={`charges-section-${si}`} className="mt-5 bg-white border border-[#E5E1D8] p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Section heading</label>
              <input value={sec.heading} onChange={(e) => setSec(si, { ...sec, heading: e.target.value })}
                className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none font-semibold" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Intro line (optional)</label>
              <input value={sec.blurb} onChange={(e) => setSec(si, { ...sec, blurb: e.target.value })}
                className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {sec.items.map((it, ii) => (
              <div key={ii} className="border border-[#F0EDE4] bg-[#FBFAF5] p-4">
                <div className="flex items-start gap-3">
                  <input value={it.description} onChange={(e) => setItem(si, ii, { ...it, description: e.target.value })}
                    placeholder="Description" className="flex-1 border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
                  <button onClick={() => setSec(si, { ...sec, items: sec.items.filter((_, j) => j !== ii) })}
                    className="p-2 text-[#58635C] hover:text-red-600"><Trash2 size={15} /></button>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                  <div className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wide text-[#58635C]">Hours</span>
                    <input type="number" step="0.5" value={it.hours ?? ""} onChange={(e) => setItem(si, ii, { ...it, hours: e.target.value === "" ? null : Number(e.target.value), amount: null })}
                      className="border border-[#E5E1D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#B89874]" /></div>
                  <div className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wide text-[#58635C]">Fixed $ (ex GST)</span>
                    <input type="number" step="0.01" value={it.amount ?? ""} onChange={(e) => setItem(si, ii, { ...it, amount: e.target.value === "" ? null : Number(e.target.value), hours: null })}
                      className="border border-[#E5E1D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#B89874]" /></div>
                  <div className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wide text-[#58635C]">Incl. GST (override)</span>
                    <input type="number" step="0.01" value={it.incl ?? ""} onChange={(e) => setItem(si, ii, { ...it, incl: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder={`auto ${inclOf(it, rate).toFixed(2)}`}
                      className="border border-[#E5E1D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#B89874]" /></div>
                  <label className="flex items-center gap-2 text-xs text-[#58635C] pb-2">
                    <input type="checkbox" checked={!!it.per_hour} onChange={(e) => setItem(si, ii, { ...it, per_hour: e.target.checked })} /> per hour
                  </label>
                </div>
                <input value={it.note || ""} onChange={(e) => setItem(si, ii, { ...it, note: e.target.value })}
                  placeholder="Extra note line (optional, e.g. indicative total)" className="mt-2 w-full border border-[#E5E1D8] px-3 py-1.5 text-sm outline-none focus:border-[#B89874]" />
              </div>
            ))}
            <button onClick={() => setSec(si, { ...sec, items: [...sec.items, { description: "", hours: null, amount: null, incl: null, per_hour: false, note: "" }] })}
              className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B]"><Plus size={14} /> Add line item</button>
          </div>
        </div>
      ))}

      <div className="mt-5 bg-white border border-[#E5E1D8] p-6">
        <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Footnotes (one per line)</label>
        <textarea data-testid="charges-footnotes" rows={5} value={(c.footnotes || []).join("\n")}
          onChange={(e) => setC({ ...c, footnotes: e.target.value.split("\n") })}
          className="mt-1.5 w-full border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y text-sm" />
      </div>
    </div>
  );
}
